/**
 * Mass-edit data model — the shapes the grid accumulates while the user edits,
 * and the projections from a grid `Row` into those shapes.
 *
 * Kept free of React/JSX imports (like ../../utils/recordingValidators) so both
 * the modal components and the save layer (massEditBatch.ts) can import it, and
 * so the diff/payload logic stays unit-testable without a DOM.
 */

import type { RecordingWestburyContributionsInner } from 'shared_components/generated/admin';
import type { MainArtistItem } from 'shared_components/utils/mainArtist';
import type {
  ArtistEntry,
  FeaturedStatus,
} from './Components/ArtistEditorModal';
import type { Row } from './MasterDiscographyTypes';

/* The multi-value fields, whose modals share one generic editor. Title and
 * Duration open modals too, but each has its own single-input editor. */
export type MassComplexField =
  | 'artist'
  | 'version'
  | 'contributions'
  | 'live'
  | 'isrcs';

export type MassLiveValue = string;
export type MassVersionValue = string[];
export type MassIsrcValue = string[];
export type MassContributionsValue = RecordingWestburyContributionsInner[];
export type MassArtistsValue = ArtistEntry[];

export type MassFieldValue =
  | MassLiveValue
  | MassVersionValue
  | MassIsrcValue
  | MassContributionsValue
  | MassArtistsValue;

/** The set of pending edits accumulated for a single recording. Keys mirror
 *  the grid column ids. */
export interface MassRowEdits {
  title?: string;
  duration?: string;
  live?: MassLiveValue;
  version?: MassVersionValue;
  isrcs?: MassIsrcValue;
  contributions?: MassContributionsValue;
  artist?: MassArtistsValue;
}

/** Pending edits across all recordings, keyed by recording id. */
export type MassEdits = Record<string, MassRowEdits>;

/** Seed an ArtistEntry[] from a recording — mirrors RecordingEditForm's
 *  initialArtists projection (handles both the new MainArtistItem objects and
 *  the legacy string/`row.artist` formats). */
export function seedArtistsFromRow(row: Row): ArtistEntry[] {
  if (row.mainArtistsRaw && row.mainArtistsRaw.length > 0) {
    return row.mainArtistsRaw.map((item) => {
      if (typeof item === 'string') {
        return {
          name: item,
          isFeatured: false,
          featuredStatus: 'nf' as FeaturedStatus,
        };
      }
      const obj = item as MainArtistItem;
      return {
        name: obj.stageName,
        id: obj.id,
        isFeatured: false,
        featuredStatus: 'nf' as FeaturedStatus,
        type: obj.type,
      };
    });
  }
  if (row.artist) {
    return row.artist
      .split(',')
      .map((name) => ({
        name: name.trim(),
        isFeatured: false,
        featuredStatus: 'nf' as FeaturedStatus,
      }))
      .filter((a) => a.name);
  }
  return [];
}
